// Shared gates for mid-year / year-end review submission.
//
// These live here (rather than in a page) so the employee flow (GoalsPage) and the
// manager flow (TeamGoalsPage) enforce the SAME rule. They previously each hand-rolled
// the check and drifted apart, which is how an employee ended up with an always-enabled
// Submit button while the manager's was correctly disabled.

/**
 * Has every KPI that actually needs a comment got one?
 *
 * Must agree with the backend gates in submit_employee_review_feedback and
 * submit_manager_review_feedback (backend/server.py), which reject a non-draft submit
 * when any KPI comment is blank. If this is stricter than the backend the button is
 * dead; if looser, the user gets a 400 on click.
 *
 * @param {Array}  goals    - goals for the employee/year (mixed statuses)
 * @param {Object} comments - map of { [kpi.id]: commentText }
 * @returns {boolean} true when every approved goal's KPIs are commented
 */
export const hasAllKpiComments = (goals, comments) => {
  // Only approved goals count, matching the backend's {"status": "approved"} query —
  // otherwise a draft/rejected goal's KPIs would disable Submit over comments the
  // backend never asks for.
  // (g.kpis || []) keeps a goal with no KPIs from making this undefined/falsy forever;
  // the backend likewise treats zero KPIs as nothing missing.
  return (goals || [])
    .filter(g => g.status === 'approved')
    .every(g => (g.kpis || []).every(kpi => comments?.[kpi.id]?.trim()));
};

/**
 * Year-end only: has every KPI that needs a rating got one?
 *
 * Mirrors the missing_ratings check in submit_manager_review_feedback
 * (backend/server.py), which requires a rating on every approved goal's KPI before a
 * non-draft year-end submit. Same approved-only / empty-safe rules as
 * hasAllKpiComments — a rating of 0 is not a valid rating here, matching the
 * backend's falsy `not ratings.get(...)` test.
 *
 * @param {Array}  goals   - goals for the employee/year (mixed statuses)
 * @param {Object} ratings - map of { [kpi.id]: ratingValue }
 * @returns {boolean} true when every approved goal's KPIs are rated
 */
export const hasAllKpiRatings = (goals, ratings) => {
  return (goals || [])
    .filter(g => g.status === 'approved')
    .every(g => (g.kpis || []).every(kpi => ratings?.[kpi.id]));
};
